/**
 * Sprint 4 — global UpgradePrompt modal trigger.
 *
 * Backend plan_guard.require_plan(...) raises HTTP 402 with a rich detail
 * payload (required_tier, current_tier, upgrade_plan, message). The axios
 * interceptor in services/api.ts catches every 402 and pushes the payload
 * to this context, which renders a modal overlay using the existing
 * UpgradePrompt component.
 *
 * Why a context (and not a singleton imported by the interceptor)? Two
 * reasons: it survives hot reload cleanly, and the modal needs hooks
 * (useNavigate, etc.) — those only work inside a React tree.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { X } from 'lucide-react'

import { UpgradePrompt } from '../components/common/UpgradePrompt'
import type { PlanTier } from '../services/billing'
import { track } from '../utils/analytics'

export interface UpgradePromptPayload {
  /** Plan the user needs in order to access the feature. */
  requiredTier: PlanTier
  /** Display name of the gated feature ("AI Insights", etc.). */
  feature: string
  /** Optional one-liner from the backend message. */
  description?: string
}

interface UpgradeModalContextValue {
  /** Open the modal with a specific payload (e.g. from an axios 402). */
  open: (payload: UpgradePromptPayload) => void
  /** Close the modal manually (the user clicking the close button). */
  close: () => void
  /** Current payload, if open. */
  current: UpgradePromptPayload | null
}

const UpgradeModalContext = createContext<UpgradeModalContextValue | null>(null)

export function UpgradeModalProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<UpgradePromptPayload | null>(null)

  const open = useCallback((payload: UpgradePromptPayload) => {
    setCurrent(payload)
    track('upgrade_prompt_shown', { required_tier: payload.requiredTier, feature: payload.feature })
  }, [])

  const close = useCallback(() => setCurrent(null), [])

  // Register a global trigger so the axios interceptor (which lives
  // outside any React component) can open the modal. We can't useEffect
  // here because the interceptor fires synchronously on module load —
  // we attach to window so first-paint already has it.
  useMemo(() => {
    (window as unknown as { __karyvaOpenUpgrade?: typeof open }).__karyvaOpenUpgrade = open
  }, [open])

  const value: UpgradeModalContextValue = { open, close, current }

  return (
    <UpgradeModalContext.Provider value={value}>
      {children}
      {current && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Upgrade required"
        >
          <div
            className="relative max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1.5 shadow hover:bg-gray-50"
              aria-label="Close upgrade prompt"
            >
              <X className="w-4 h-4" />
            </button>
            <UpgradePrompt
              requiredTier={current.requiredTier}
              feature={current.feature}
              description={current.description}
            />
          </div>
        </div>
      )}
    </UpgradeModalContext.Provider>
  )
}

/** Hook for components that want to programmatically trigger the modal. */
export function useUpgradeModal(): UpgradeModalContextValue {
  const ctx = useContext(UpgradeModalContext)
  if (!ctx) {
    throw new Error('useUpgradeModal must be used inside UpgradeModalProvider')
  }
  return ctx
}

/**
 * Called by the axios interceptor when a 402 lands. Looks up the global
 * trigger registered by UpgradeModalProvider; no-op if the provider
 * hasn't mounted yet (e.g. during the login flow before the app tree
 * boots).
 */
export function triggerUpgradeModalFromInterceptor(payload: UpgradePromptPayload): boolean {
  const trigger = (window as unknown as { __karyvaOpenUpgrade?: (p: UpgradePromptPayload) => void })
    .__karyvaOpenUpgrade
  if (typeof trigger === 'function') {
    trigger(payload)
    return true
  }
  return false
}
